/**
 * TransactionalTemplatesPage — Phase 8 Prompt 7 implementation.
 *
 * Per audit 05D_TEMPLATE_MANAGER.md (transactional mode).
 *
 * Full CRUD parity with Marketing Templates (Preview · Edit · Duplicate · New).
 * Transactional templates are operational must-deliver emails — edits go live
 * immediately, so the editor surfaces required merge variables and the page
 * shows an operational caution banner.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  Skeleton,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import PillarPageShell from "../shared/PillarPageShell";
import { ActionButton, StatusPill, StarterChooser, type StarterEntry } from "../../ops";
import { EmptyState } from "../../help";
import apiService from "../../../services/api";
import TransactionalTemplateEditorDialog from "./TransactionalTemplateEditorDialog";
import {
  isTransactionalTemplate,
  parseEmailTemplatesListResponse,
  transactionalFormFromApiRow,
} from "./transactionalTemplateUtils";

interface TemplateRow {
  id: string;
  template_key: string;
  template_name: string;
  category: string;
  status: string;
  is_active: boolean;
  brevo_sync_status: string;
  times_sent: number;
  last_sent_at: string | null;
  kind: "transactional" | "nurture";
  sender_track: string;
}

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "auth_security", label: "🔐 Auth & Security", legacyMatch: ["authentication"] },
  { id: "subscription_lifecycle", label: "💳 Subscription", legacyMatch: ["subscription"] },
  { id: "family_vault", label: "🔒 Family Vault", legacyMatch: ["collaboration"] },
  { id: "family_hub", label: "👨‍👩‍👧 Family Hub", legacyMatch: ["collaboration"] },
  { id: "gift_subscriptions", label: "🎁 Gift Subs", legacyMatch: ["administrative"] },
  { id: "content_product", label: "📖 Content", legacyMatch: ["engagement", "notification"] },
  {
    id: "admin_system",
    label: "⚙️ Admin & System",
    legacyMatch: ["administrative", "admin_system"],
  },
];

const TRANSACTIONAL_TEMPLATES_BASE =
  "/dashboard/communications/transactional/templates";

const TransactionalTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ "*"?: string }>();
  const currentCategory = (params["*"] ?? "all").split("/")[0] || "all";

  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [chooserOpen, setChooserOpen] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editorTemplateId, setEditorTemplateId] = useState<string | null>(null);
  const [editorStarter, setEditorStarter] = useState<StarterEntry | null>(null);
  const [editorInitialTab, setEditorInitialTab] = useState(0);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get(
        "/api/admin/email-templates?kind=transactional&limit=500",
      );
      const rows = parseEmailTemplatesListResponse(res) as TemplateRow[];
      setTemplates(rows.filter((r) => isTransactionalTemplate(r)));
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Failed to load templates";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredRows = useMemo(() => {
    let rows = templates;
    if (currentCategory !== "all") {
      const cat = CATEGORIES.find((c) => c.id === currentCategory);
      if (cat) {
        rows = rows.filter(
          (r) =>
            r.category === currentCategory ||
            (cat.legacyMatch && cat.legacyMatch.includes(r.category)),
        );
      }
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.template_key.toLowerCase().includes(q) ||
          (r.template_name?.toLowerCase() ?? "").includes(q),
      );
    }
    return rows;
  }, [templates, currentCategory, search]);

  const openCreateFromStarter = (starter: StarterEntry) => {
    setEditorMode("create");
    setEditorTemplateId(null);
    setEditorStarter(starter);
    setEditorInitialTab(0);
    setEditorOpen(true);
  };

  const openEdit = (row: TemplateRow, tab = 0) => {
    setEditorMode("edit");
    setEditorTemplateId(row.id);
    setEditorStarter(null);
    setEditorInitialTab(tab);
    setEditorOpen(true);
  };

  const openDuplicate = async (row: TemplateRow) => {
    try {
      const res: any = await apiService.get(
        `/api/admin/email-templates/${row.id}`,
      );
      const data = transactionalFormFromApiRow(res?.data ?? res);
      setEditorMode("create");
      setEditorTemplateId(null);
      setEditorStarter({
        id: `${data.template_key}_copy`,
        name: `${data.template_name} (copy)`,
        useCase: data.description,
        category: data.category,
        subjectScaffold: data.subject_line,
        previewTextScaffold: data.preview_text,
        html_content: data.html_content,
        plain_text_content: data.plain_text_content,
        required_variables: data.required_variables,
      });
      setEditorInitialTab(0);
      setEditorOpen(true);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load template for duplicate");
    }
  };

  const handleStarterSelect = (starter: StarterEntry) => {
    setChooserOpen(false);
    openCreateFromStarter(starter);
  };

  return (
    <PillarPageShell
      pillar="transactional"
      title="Transactional Templates"
      subtitle={
        templates.length > 0
          ? `${templates.length} operational must-deliver templates.`
          : "Operational must-deliver email templates."
      }
      helpTipId="template.kind.badge"
      rightActions={
        <ActionButton
          verb="duplicate"
          labelOverride="+ New Template"
          onClick={() => setChooserOpen(true)}
          showIcon={false}
          variantOverride="primary"
        />
      }
    >
      <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
        <Typography variant="body2">
          ⚠️ <strong>Operational emails.</strong> Transactional templates are
          must-deliver messages (password resets, receipts, security alerts).
          Edits go live immediately — preserve required merge variables (reset
          links, verification codes) when editing.
        </Typography>
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={currentCategory}
          onChange={(_, v) =>
            navigate(
              v === "all"
                ? TRANSACTIONAL_TEMPLATES_BASE
                : `${TRANSACTIONAL_TEMPLATES_BASE}/${v}`,
            )
          }
          variant="scrollable"
          scrollButtons="auto"
        >
          {CATEGORIES.map((c) => (
            <Tab
              key={c.id}
              value={c.id}
              label={c.label}
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: "0.8rem",
                fontWeight: 600,
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by template_key or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={48} />
              ))}
            </Box>
          ) : filteredRows.length === 0 ? (
            <EmptyState
              surface="templates.empty.transactional"
              onCreate={() => setChooserOpen(true)}
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Template Key</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Brevo</TableCell>
                  <TableCell align="right">Sends</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openEdit(row)}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                      >
                        {row.template_key}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {row.template_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="T"
                        size="small"
                        sx={{
                          bgcolor: "#5D6D7E",
                          color: "white",
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          height: 20,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        status={row.is_active ? "active" : "archived"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {row.brevo_sync_status ?? "local_only"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.times_sent ?? 0}</TableCell>
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          gap: 0.5,
                          justifyContent: "flex-end",
                        }}
                      >
                        <ActionButton
                          verb="preview"
                          size="small"
                          showIcon={false}
                          onClick={() => openEdit(row, 2)}
                        />
                        <ActionButton
                          verb="edit"
                          size="small"
                          showIcon={false}
                          onClick={() => openEdit(row, 1)}
                        />
                        <ActionButton
                          verb="duplicate"
                          size="small"
                          showIcon={false}
                          onClick={() => openDuplicate(row)}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <StarterChooser
        entityType="template"
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onSelect={handleStarterSelect}
      />

      <TransactionalTemplateEditorDialog
        open={editorOpen}
        mode={editorMode}
        templateId={editorTemplateId}
        starter={editorStarter}
        initialTab={editorInitialTab}
        onClose={() => {
          setEditorOpen(false);
          setEditorStarter(null);
        }}
        onSaved={loadTemplates}
      />
    </PillarPageShell>
  );
};

export default TransactionalTemplatesPage;
